// ── Faction & Reputation system types ──
/** Faction identifiers */
export var FactionId;
(function (FactionId) {
    FactionId["IronguardOrder"] = "ironguard_order";
    FactionId["ArcaneSanctum"] = "arcane_sanctum";
    FactionId["ShadowGuild"] = "shadow_guild";
    FactionId["WildcallPact"] = "wildcall_pact";
})(FactionId || (FactionId = {}));
/** Rival faction reputation penalty ratio (gaining 10 rep loses 3 rival rep) */
export const RIVAL_REPUTATION_PENALTY_RATIO = 0.3;
/** Starting reputation with all factions */
export const DEFAULT_FACTION_REPUTATION = 0;
/** Reputation boundaries */
export const MIN_REPUTATION = -1000;
export const MAX_REPUTATION = 10000;
// ── Faction definitions (data-driven) ──
export const FACTION_DEFINITIONS = [
    {
        id: FactionId.IronguardOrder,
        name: 'Ironguard Order',
        description: 'Stalwart defenders and master smiths. They value strength and honor.',
        rivalFactions: [FactionId.ShadowGuild],
        tiers: [
            { level: -2, name: 'Hostile', minReputation: -500, rewards: [] },
            { level: -1, name: 'Unfriendly', minReputation: -200, rewards: [] },
            { level: 0, name: 'Neutral', minReputation: 0, rewards: [] },
            { level: 1, name: 'Friendly', minReputation: 500, rewards: [
                    { type: 'shop_discount', rewardId: 'ironguard_shop_5', value: 5 },
                ] },
            { level: 2, name: 'Honored', minReputation: 1500, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_ironguard_blade' },
                    { type: 'shop_discount', rewardId: 'ironguard_shop_10', value: 10 },
                ] },
            { level: 3, name: 'Revered', minReputation: 3500, rewards: [
                    { type: 'stat_bonus', rewardId: 'ironguard_defense', value: 5 },
                    { type: 'title', rewardId: 'Ironguard Champion' },
                ] },
            { level: 4, name: 'Exalted', minReputation: 7000, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_ironguard_plate' },
                    { type: 'cosmetic', rewardId: 'ironguard_aura' },
                ] },
        ],
    },
    {
        id: FactionId.ArcaneSanctum,
        name: 'Arcane Sanctum',
        description: 'Keepers of forbidden knowledge and arcane power.',
        rivalFactions: [FactionId.WildcallPact],
        tiers: [
            { level: -2, name: 'Hostile', minReputation: -500, rewards: [] },
            { level: -1, name: 'Unfriendly', minReputation: -200, rewards: [] },
            { level: 0, name: 'Neutral', minReputation: 0, rewards: [] },
            { level: 1, name: 'Friendly', minReputation: 500, rewards: [
                    { type: 'shop_discount', rewardId: 'arcane_shop_5', value: 5 },
                ] },
            { level: 2, name: 'Honored', minReputation: 1500, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_arcane_orb' },
                ] },
            { level: 3, name: 'Revered', minReputation: 3500, rewards: [
                    { type: 'stat_bonus', rewardId: 'arcane_mana', value: 20 },
                    { type: 'title', rewardId: 'Arcane Scholar' },
                ] },
            { level: 4, name: 'Exalted', minReputation: 7000, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_arcane_staff' },
                    { type: 'cosmetic', rewardId: 'arcane_aura' },
                ] },
        ],
    },
    {
        id: FactionId.ShadowGuild,
        name: 'Shadow Guild',
        description: 'Masters of stealth and subterfuge. They trade in secrets and daggers.',
        rivalFactions: [FactionId.IronguardOrder],
        tiers: [
            { level: -2, name: 'Hostile', minReputation: -500, rewards: [] },
            { level: -1, name: 'Unfriendly', minReputation: -200, rewards: [] },
            { level: 0, name: 'Neutral', minReputation: 0, rewards: [] },
            { level: 1, name: 'Friendly', minReputation: 500, rewards: [
                    { type: 'shop_discount', rewardId: 'shadow_shop_5', value: 5 },
                ] },
            { level: 2, name: 'Honored', minReputation: 1500, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_shadow_cloak' },
                ] },
            { level: 3, name: 'Revered', minReputation: 3500, rewards: [
                    { type: 'stat_bonus', rewardId: 'shadow_crit', value: 8 },
                    { type: 'title', rewardId: 'Shadow Blade' },
                ] },
            { level: 4, name: 'Exalted', minReputation: 7000, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_shadow_dagger' },
                    { type: 'cosmetic', rewardId: 'shadow_aura' },
                ] },
        ],
    },
    {
        id: FactionId.WildcallPact,
        name: 'Wildcall Pact',
        description: 'Druids and beast tamers who commune with the wilds.',
        rivalFactions: [FactionId.ArcaneSanctum],
        tiers: [
            { level: -2, name: 'Hostile', minReputation: -500, rewards: [] },
            { level: -1, name: 'Unfriendly', minReputation: -200, rewards: [] },
            { level: 0, name: 'Neutral', minReputation: 0, rewards: [] },
            { level: 1, name: 'Friendly', minReputation: 500, rewards: [
                    { type: 'shop_discount', rewardId: 'wildcall_shop_5', value: 5 },
                ] },
            { level: 2, name: 'Honored', minReputation: 1500, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_wildcall_bow' },
                ] },
            { level: 3, name: 'Revered', minReputation: 3500, rewards: [
                    { type: 'stat_bonus', rewardId: 'wildcall_speed', value: 15 },
                    { type: 'title', rewardId: 'Beastcaller' },
                ] },
            { level: 4, name: 'Exalted', minReputation: 7000, rewards: [
                    { type: 'recipe_unlock', rewardId: 'recipe_wildcall_armor' },
                    { type: 'cosmetic', rewardId: 'wildcall_aura' },
                ] },
        ],
    },
];
// ── Messages ──
/** Server → Client: faction state sync */
export const MSG_FACTION_SYNC = 'faction_sync';
/** Server → Client: reputation changed */
export const MSG_REPUTATION_CHANGE = 'reputation_change';
/** Client → Server: claim faction reward */
export const MSG_FACTION_CLAIM_REWARD = 'faction_claim_reward';
export const MSG_FACTION_CLAIM_RESULT = 'faction_claim_result';
//# sourceMappingURL=factions.js.map